import { useEffect, useMemo, useState } from "react";
import { MapPin, UtensilsCrossed } from "lucide-react";
import { api } from "@/lib/api";
import { useLang, usePick } from "@/i18n/LanguageContext";
import Reveal, { Caption } from "@/components/Reveal";

export default function RestaurantsPage() {
  const { t } = useLang();
  const pick = usePick();
  const [items, setItems] = useState([]);
  const [cuisine, setCuisine] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/restaurants").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const cuisines = useMemo(() => ["All", ...new Set(items.map((i) => i.cuisine))], [items]);
  const filtered = cuisine === "All" ? items : items.filter((i) => i.cuisine === cuisine);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-24" data-testid="restaurants-page">
      <Reveal>
        <Caption>{t("restaurants.caption")}</Caption>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mt-4 text-ink max-w-3xl">
          {t("restaurants.titleA")} <span className="italic text-terracotta">{t("restaurants.titleB")}</span>
        </h1>
        <p className="text-ink/60 max-w-2xl mt-6 leading-relaxed">{t("restaurants.copy")}</p>
      </Reveal>

      <div className="flex flex-wrap gap-2 mt-10" data-testid="restaurant-filters">
        {cuisines.map((c) => (
          <button
            key={c}
            data-testid={`cuisine-${c.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
            onClick={() => setCuisine(c)}
            className={`rounded-full px-5 py-2 text-sm font-medium border transition-colors ${
              cuisine === c ? "bg-night text-sand border-night" : "border-ink/15 hover:border-terracotta text-ink/70"
            }`}
          >
            {c === "All" ? t("restaurants.all") : c}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="mt-16 text-ink/40 text-sm" data-testid="restaurants-loading">{t("restaurants.loading")}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10" data-testid="restaurants-grid">
          {filtered.map((r, i) => (
            <Reveal key={r.id} delay={(i % 2) * 0.08}>
              <article
                data-testid={`restaurant-card-${r.id}`}
                className="group bg-white rounded-3xl overflow-hidden shadow-[0_2px_24px_rgba(22,27,34,0.07)] border border-terracotta/10 grid grid-cols-1 sm:grid-cols-5 hover:-translate-y-1.5 transition-transform duration-300"
              >
                <div className="sm:col-span-2 h-52 sm:h-full overflow-hidden">
                  <img src={r.image} alt={r.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="sm:col-span-3 p-6 flex flex-col gap-3">
                  <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.15em]">
                    <span className="text-teal">{r.cuisine}</span>
                    <span className="text-ink/30">·</span>
                    <span className="text-terracotta font-semibold">{r.price_level}</span>
                    <span className="text-ink/30">·</span>
                    <span className="text-ink/50">{r.area}</span>
                  </div>
                  <h3 className="font-serif text-2xl text-ink leading-snug">{r.name}</h3>
                  <p className="text-sm text-ink/60 leading-relaxed">{pick(r, "description")}</p>
                  <p className="text-sm text-ink flex items-start gap-2 mt-auto">
                    <UtensilsCrossed size={15} className="text-gold mt-0.5 shrink-0" />
                    <span className="italic">{t("restaurants.order")} {pick(r, "recommended_dish")}</span>
                  </p>
                  {r.maps_url && (
                    <a
                      href={r.maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`restaurant-directions-${r.id}`}
                      className="text-sm font-semibold text-terracotta hover:text-terracotta-dark flex items-center gap-1.5 transition-colors"
                    >
                      <MapPin size={15} /> {t("restaurants.directions")}
                    </a>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}
